import React, { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import {
  Map,
  Code,
  Users,
  Printer,
  Target,
  Award,
  CheckCircle,
  ArrowRight,
  Globe,
  Cpu,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Counter component for animated numbers
const Counter = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.5 });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.ceil(start));
        }
      }, 1000 / 60);

      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}+</span>;
};

const AboutSnapshot = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });

  const stats = [
    {
      number: 50,
      label: "Projects Completed",
      icon: <Target className="w-6 h-6" />,
    },
    {
      number: 25,
      label: "Clients Served",
      icon: <Users className="w-6 h-6" />,
    },
    { number: 15, label: "Team Experts", icon: <Award className="w-6 h-6" /> },
    { number: 5, label: "Years Experience", icon: <Cpu className="w-6 h-6" /> },
  ];

  const services = [
    {
      icon: <Map className="w-8 h-8" />,
      title: "GIS Services",
      description: "Precision mapping, surveying, and remote sensing solutions",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Software Services",
      description: "Full-stack IT solutions tailored for your business needs",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-gradient-to-br from-green-50 to-emerald-50",
      borderColor: "border-green-200",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Consulting",
      description:
        "Expert guidance across environmental, mining, and IT sectors",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
    },
    {
      icon: <Printer className="w-8 h-8" />,
      title: "Printing & Scanning",
      description: "High-quality technical printing and digital archiving",
      color: "from-orange-500 to-red-500",
      bgColor: "bg-gradient-to-br from-orange-50 to-red-50",
      borderColor: "border-orange-200",
    },
  ];

  const values = [
    "Experienced Team of Specialists",
    "Latest Technology & Equipment",
    "Accurate & Reliable Results",
    "Trusted by Industry Leaders",
    "End-to-End Solution Provider",
    "Sustainable & Innovative Approaches",
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-4 lg:py-8 bg-white overflow-hidden"
    >
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center max-w-4xl mx-auto mb-10 lg:mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={
              isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-brandBlue/10 to-brandGreen/10 border border-brandBlue/20 mb-4"
          >
            <Globe className="w-4 h-4 text-brandBlue mr-2" />
            <span className="text-sm font-semibold text-brandText">
              About Maptech Solutions
            </span>
          </motion.div>

          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-primaryTextColor mb-8 font-lexend">
            Intelligence in{" "}
            <span className="bg-gradient-to-r from-brandBlue to-brandGreen bg-clip-text text-transparent">
              Every Map
            </span>
          </h2>

          <p className="text-lg lg:text-xl text-secondaryTextColor leading-relaxed">
            Leading provider of{" "}
            <span className="text-primaryTextColor font-semibold">
              GIS, IT, Consulting, and Printing
            </span>{" "}
            solutions that drive innovation and precision across multiple
            sectors.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start mb-10">
          {/* Left Content - Stats */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-2 gap-5"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative p-5 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:scale-105 shadow-sm hover:shadow-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brandBlue/5 to-brandGreen/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-brandBlue opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                      {stat.icon}
                    </div>
                    <div className="text-3xl lg:text-4xl font-bold text-primaryTextColor font-lexend">
                      <Counter end={stat.number} duration={2.5} />
                    </div>
                  </div>
                  <div className="text-sm lg:text-base text-secondaryTextColor font-medium">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Right Content - Description */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-4"
          >
            <p className="text-base lg:text-lg text-secondaryTextColor leading-relaxed">
              <span className="text-primaryTextColor font-semibold">
                Maptech Solutions Pvt Ltd
              </span>{" "}
              specializes in delivering comprehensive spatial solutions and IT
              services that drive efficiency, sustainability, and growth for
              businesses across forestry, mining, environmental management, and
              urban planning.
            </p>

            <p className="text-base lg:text-lg text-secondaryTextColor leading-relaxed">
              Our team combines deep industry expertise with cutting-edge
              technology to provide end-to-end solutions that meet the evolving
              needs of modern enterprises, ensuring precision and innovation in
              every project.
            </p>

            {/* Values List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={
                    isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }
                  }
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <CheckCircle className="w-5 h-5 text-brandGreen flex-shrink-0" />
                  <span className="text-secondaryTextColor text-sm lg:text-base">
                    {value}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Services Overview */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mb-10"
        >
          <h3 className="text-2xl lg:text-3xl font-bold text-primaryTextColor text-center mb-8 font-lexend">
            Our Core Services
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                className={`group relative p-5 rounded-2xl ${service.bgColor} border ${service.borderColor} hover:border-brandBlue/50 transition-all duration-500 hover:scale-105 shadow-sm hover:shadow-lg`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                ></div>

                <div className="relative z-10">
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform duration-300 shadow-md`}
                  >
                    {service.icon}
                  </div>

                  <h4 className="text-lg font-bold text-primaryTextColor mb-2 font-lexend">
                    {service.title}
                  </h4>

                  <p className="text-secondaryTextColor text-sm leading-relaxed mb-3">
                    {service.description}
                  </p>

                  <div className="flex items-center text-brandBlue text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Learn more <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative"
        >
          <div className="relative bg-gradient-to-br from-slate-900 via-brandBlue to-brandGreen rounded-3xl p-8 lg:p-10 text-white overflow-hidden">
            {/* Background Elements from Option 1 */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>

            <div className="relative text-center max-w-3xl mx-auto">
              {/* Icon from Option 2 */}
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
                <Target className="w-8 h-8 text-white" />
              </div>

              {/* Heading from Option 2 with gradient text */}
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 font-lexend">
                Transform Your Business with{" "}
                <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                  Maptech Solutions
                </span>
              </h3>

              {/* Text from Option 2 */}
              <p className="text-white/80 text-base lg:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                Ready to leverage cutting-edge spatial intelligence and IT
                solutions? Let's discuss how we can drive real results for your
                business.
              </p>

              {/* Buttons from Option 2 */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                <motion.button
                  onClick={() => navigate("/contact")}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3.5 bg-white text-slate-900 font-semibold rounded-xl hover:shadow-xl transition-all duration-300 shadow-md w-full sm:w-auto flex items-center gap-2 group"
                >
                  Get Started Today
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </motion.button>

                <motion.button
                  onClick={() => navigate("/about")}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3.5 bg-transparent text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white hover:text-slate-900 transition-all duration-300 w-full sm:w-auto backdrop-blur-sm flex items-center gap-2"
                >
                  Meet our Team
                </motion.button>
              </div>

              {/* Trust signals from Option 2 */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/70">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-300" />
                  No commitment
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-300" />
                  Free consultation
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-300" />
                  24/7 support
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSnapshot;
